import { useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

const TOKEN_RE = /@([\p{L}\p{N}._-]*)$/u;

export default function CommentBox({ users = [], onSubmit, busy = false, testidPrefix = "comentario" }) {
  const [texto, setTexto] = useState("");
  const [mentions, setMentions] = useState([]);
  const [query, setQuery] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const ref = useRef(null);

  const suggestions =
    query === null
      ? []
      : users.filter((u) => u.nombre.toLowerCase().includes(query.toLowerCase())).slice(0, 6);

  const handleChange = (e) => {
    const val = e.target.value;
    setTexto(val);
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const match = upto.match(TOKEN_RE);
    setQuery(match ? match[1] : null);
    setActiveIdx(0);
  };

  const insertMention = (u) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : texto.length;
    const upto = texto.slice(0, caret);
    const rest = texto.slice(caret);
    const newUpto = upto.replace(TOKEN_RE, `@${u.nombre} `);
    const newText = newUpto + rest;
    setTexto(newText);
    setMentions((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, { id: u.id, nombre: u.nombre }]));
    setQuery(null);
    setTimeout(() => {
      if (el) {
        el.focus();
        const pos = newUpto.length;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (query !== null && suggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(suggestions[activeIdx]); return; }
      if (e.key === "Escape") { setQuery(null); return; }
    }
  };

  const enviar = () => {
    const t = texto.trim();
    if (!t) return;
    // Solo cuentan como mención los usuarios REALES seleccionados de la lista y cuyo
    // "@Nombre" siga presente en el texto. Texto libre tras "@" nunca es mención.
    const activas = mentions.filter((m) => users.some((u) => u.id === m.id) && t.includes(`@${m.nombre}`));
    onSubmit(t, activas);
    setTexto("");
    setMentions([]);
    setQuery(null);
  };

  return (
    <div className="relative">
      {query !== null && suggestions.length > 0 && (
        <div
          className="absolute bottom-full mb-1 left-0 z-30 w-64 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          data-testid={`${testidPrefix}-mention-list`}
        >
          {suggestions.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
              data-testid={`${testidPrefix}-mention-option-${u.id}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                i === activeIdx ? "bg-violet-50 text-violet-700" : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold shrink-0">
                {u.nombre?.[0]?.toUpperCase()}
              </span>
              {u.nombre}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <Textarea
          ref={ref}
          rows={2}
          value={texto}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Escribe un comentario… usa @ para mencionar"
          data-testid={`${testidPrefix}-input`}
          className="flex-1"
        />
        <Button onClick={enviar} disabled={busy || !texto.trim()} data-testid={`${testidPrefix}-enviar`} className="rounded-lg">
          <Send size={15} />
        </Button>
      </div>
    </div>
  );
}
